import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import DataTable from '../../components/admin/DataTable';
import { Search, Anchor } from 'lucide-react';
import { format } from 'date-fns';

export default function AdminRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;

      const response = await adminAPI.getAllRequests(params);
      setRequests(response.data || []);
      setTotalPages(response.meta?.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterStatus]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchRequests();
  };

  const columns = [
    {
      header: 'Port',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Anchor className="h-4 w-4 text-primary-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-navy-900">{row.port_name || 'Unknown'}</p>
            {row.berth_number && <p className="text-xs text-navy-500">Berth: {row.berth_number}</p>}
          </div>
        </div>
      ),
    },
    {
      header: 'Owner',
      render: (row) => (
        <div>
          <p className="text-sm text-navy-900">{row.owner_first_name} {row.owner_last_name}</p>
          <p className="text-xs text-navy-500">{row.owner_email}</p>
        </div>
      ),
    },
    {
      header: 'Yacht',
      render: (row) => (
        <div className="text-sm text-navy-700">
          {row.yacht_name || '-'}
          {row.length_m ? <span className="text-xs text-navy-400 ml-1">({row.length_m}m)</span> : ''}
        </div>
      ),
    },
    {
      header: 'Date',
      render: (row) => (
        <div className="text-xs text-navy-600">
          <p>{row.scheduled_date || '-'}</p>
          {row.scheduled_time && <p className="text-navy-400">{row.scheduled_time}</p>}
        </div>
      ),
    },
    {
      header: 'Status',
      render: (row) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(row.status)}`}>
          {row.status?.replace('_', ' ')}
          {row.is_urgent ? ' (urgent)' : ''}
        </span>
      ),
    },
    {
      header: 'Apps',
      render: (row) => (
        <span className="text-sm text-navy-700">{row.application_count || 0}</span>
      ),
    },
    {
      header: 'Services',
      render: (row) => (
        <span className="text-sm text-navy-700">{row.service_count || 0}</span>
      ),
    },
    {
      header: 'Created',
      render: (row) => (
        <span className="text-xs text-navy-500">
          {row.created_at ? format(new Date(row.created_at), 'MMM d, yyyy') : '-'}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-semibold text-navy-900">Request Monitoring</h1>
        <p className="text-navy-500 mt-1">Monitor all mooring requests on the platform.</p>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by port, owner name, or email..."
            className="input-field pl-10 w-full"
          />
        </form>
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          className="input-field w-auto"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="matched">Matched</option>
          <option value="confirmed">Confirmed</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={requests}
        loading={loading}
        emptyMessage="No requests found"
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
}

function statusColor(status) {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    active: 'bg-blue-100 text-blue-800',
    matched: 'bg-indigo-100 text-indigo-800',
    confirmed: 'bg-primary-100 text-primary-800',
    in_progress: 'bg-green-100 text-green-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-navy-100 text-navy-700';
}
